import type { ThreadData } from '@liveblocks/client'
import _ from 'lodash'

import { AnalysisQuote, AnalysisResult } from '@/gen/controls/v1/analysis_result_pb'
import { RiskQuestionnaireQuestionThreadActivity } from '@/gen/inventory/v1/control_service_pb'

import { sortByTimestamp } from '@/lib/date/date'

import { assertUnreachable } from '@/utils/type-utils'

import type {
  StatusChange,
  ThreadTimelineEntry,
  ThreadTimelineItem,
} from '@/pages/control/risk-questionnaire-thread-card/types'

import { analysisQuoteTitle } from '@/components/shared/analysis-quotes/utils'
import { CommentMetadata } from '@/components/shared/comment/types'

export const carriesDecision = (activity: RiskQuestionnaireQuestionThreadActivity) =>
  activity.isLemaSuggestionAccepted !== undefined || activity.markedAsResolved !== undefined

const isLiveThread = (item: ThreadTimelineItem): item is ThreadData<CommentMetadata> =>
  !('$typeName' in item)

const bothDated = (left?: AnalysisResult, right?: AnalysisResult) =>
  !!left?.createdAt && !!right?.createdAt

export const groupConsecutiveEvaluations = (items: ThreadTimelineItem[]): ThreadTimelineEntry[] => {
  const entries: ThreadTimelineEntry[] = []
  for (const item of items) {
    if (isLiveThread(item)) {
      entries.push({ kind: 'liveThread', thread: item })
      continue
    }
    switch (item.$typeName) {
      case 'inventory.v1.RiskQuestionnaireQuestionThreadActivity':
        entries.push({ kind: 'activity', activity: item })
        break
      case 'controls.v1.AnalysisResult': {
        const run = _.last(entries)
        if (run?.kind === 'evaluations' && bothDated(_.last(run.evaluations), item)) {
          run.evaluations.push(item)
        } else {
          entries.push({ kind: 'evaluations', evaluations: [item] })
        }
        break
      }
      default:
        assertUnreachable(item)
    }
  }
  return entries
}

export const timelineEntryKey = (entry: ThreadTimelineEntry): string => {
  switch (entry.kind) {
    case 'evaluations':
      return `evaluations-${_.last(entry.evaluations)?.id}`
    case 'activity':
      return `activity-${entry.activity.id}`
    case 'commentSlot':
      return `comment-slot-${entry.activity.id}`
    case 'liveThread':
      return `thread-${entry.thread.id}`
    default:
      return assertUnreachable(entry)
  }
}

export const statusChangesById = (evaluations: AnalysisResult[]): Map<string, StatusChange> => {
  const changes = new Map<string, StatusChange>()
  let previous: AnalysisResult | undefined
  for (const evaluation of sortByTimestamp(evaluations, (item) => item.createdAt).reverse()) {
    if (previous && previous.status !== evaluation.status) {
      changes.set(evaluation.id, { from: previous.status, to: evaluation.status })
    }
    previous = evaluation
  }
  return changes
}

export const countStatusChanges = (
  evaluations: AnalysisResult[],
  statusChanges: Map<string, StatusChange>,
) => evaluations.filter((evaluation) => statusChanges.has(evaluation.id)).length

export const citedArtifactKey = (quote: AnalysisQuote): string | undefined => {
  if (quote.quote.case !== 'documentQuote') return undefined
  const documentQuote = quote.quote.value
  switch (documentQuote.quote.case) {
    case 'orgArtifactId':
      return `org-${documentQuote.quote.value}`
    case 'artifact':
      return `public-${documentQuote.quote.value.id}`
    case undefined:
      return undefined
    default:
      return assertUnreachable(documentQuote.quote)
  }
}

export const citedArtifactQuotes = (quotes: AnalysisQuote[]): AnalysisQuote[] =>
  quotes.filter((quote) => citedArtifactKey(quote) !== undefined)

export const citedArtifactNames = (quotes: AnalysisQuote[]): string[] =>
  _.uniq(citedArtifactQuotes(quotes).map(analysisQuoteTitle))
